import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Send, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { localizePath } from '../i18n/paths';

const PackageRequestModal = ({ isOpen, onClose, packageInfo }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefono: '',
    tipo_attivita: '',
    messaggio: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim() || !formData.email.trim() || !formData.telefono.trim() || !formData.tipo_attivita.trim()) {
      setError(t('modal.error_required'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('modal.error_email'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`/api/package-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          pacchetto_nome: packageInfo.nome,
          pacchetto_iniziale: packageInfo.iniziale,
          pacchetto_mensile: packageInfo.mensile,
          pacchetto_dettagli: packageInfo.dettagli || '',
          lang: (i18n.language || 'it').split('-')[0]
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const detail = errorData?.detail || `Error ${response.status}`;
        throw new Error(detail);
      }

      onClose();
      const lang = (i18n.language || 'it').split('-')[0];
      navigate(localizePath('/', lang), { state: { showSuccess: true, packageName: packageInfo.nome } });
      
    } catch (err) {
      setError(t('modal.error_generic', { message: err.message }));
      console.error('Package request error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const tipiAttivita = [
    t('contatti.form.tipi.ristorante_pizzeria_bar'),
    t('contatti.form.tipi.hotel'),
    t('contatti.form.tipi.negozio'),
    t('contatti.form.tipi.agenzia_servizi')
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="modal-backdrop"
          />
          
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-[var(--color-surface)] rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="package-request-modal"
            >
              <div className="sticky top-0 bg-[var(--color-surface)] border-b border-[var(--color-border)] p-5 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[var(--color-text)]">
                    {t('modal.title', { name: packageInfo.nome })}
                  </h2>
                  <p className="text-sm text-[var(--color-text-muted)] mt-1">
                    {packageInfo.iniziale}€ + {packageInfo.mensile}€{t('tariffe.per_month')}
                  </p>
                </div>
                <button 
                  onClick={onClose}
                  className="p-2 hover:bg-[var(--color-bg)] rounded-full transition-colors"
                  data-testid="close-modal-btn"
                >
                  <X className="w-5 h-5 text-[var(--color-text-muted)]" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1.5">
                    {t('modal.nome_label')}
                  </label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    placeholder={t('modal.nome_placeholder')}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                    data-testid="input-nome"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1.5">
                    {t('modal.email_label')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('modal.email_placeholder')}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1.5">
                    {t('modal.telefono_label')}
                  </label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder={t('modal.telefono_placeholder')}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                    data-testid="input-telefono"
                  />
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1.5">
                    {t('modal.attivita_label')}
                  </label>
                  <select
                    name="tipo_attivita"
                    value={formData.tipo_attivita}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)] transition-colors appearance-none cursor-pointer relative z-[60]"
                    style={{ position: 'relative' }}
                    data-testid="select-tipo-attivita"
                  >
                    <option value="">{t('modal.attivita_placeholder')}</option>
                    {tipiAttivita.map(tipo => (
                      <option key={tipo} value={tipo}>{tipo}</option>
                    ))}
                  </select>
                  <div className="absolute right-4 top-[calc(50%+0.75rem)] -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-[var(--color-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-1.5">
                    {t('modal.messaggio_label')} <span className="text-[var(--color-text-muted)]">{t('modal.messaggio_optional')}</span>
                  </label>
                  <textarea
                    name="messaggio"
                    value={formData.messaggio}
                    onChange={handleChange}
                    placeholder={t('modal.messaggio_placeholder')}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-primary)] transition-colors resize-none"
                    data-testid="input-messaggio"
                  />
                </div>

                {error && (
                  <motion.div 
                    className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-sm"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {error}
                  </motion.div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary h-12 text-base"
                  data-testid="submit-request-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t('common.invio_in_corso')}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {t('modal.submit')}
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-[var(--color-text-muted)]">
                  {t('modal.footer_note')}
                </p>
              </form>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PackageRequestModal;
